/**
 * Purge Statistics Component
 *
 * Displays two statistics cards:
 * - Lifetime Data Deleted
 * - Selected Range Data Deleted
 */

import React, { useState, useEffect } from 'react';
import { Database, Trash } from 'lucide-react';
import { formatNumberWithCommas } from '../../../utils/purge-utils';

export interface PurgeStatisticsProps {
  lifetimeTotal: number;
  selectedRangeTotal: number;
  isLoadingLifetime?: boolean;
  isLoadingSelectedRange?: boolean;
}

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  iconBgColor: string;
  iconColor: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  title,
  value,
  iconBgColor,
  iconColor,
  isLoading = false,
}) => {
  const [randomValue, setRandomValue] = useState(0);

  useEffect(() => {
    if (isLoading) {
      // Reset to 0 when loading starts
      setRandomValue(0);

      // Increment number every 100ms while loading with variability
      const interval = setInterval(() => {
        setRandomValue((prev) => {
          // Random increment between 50,000 and 150,000
          const increment = Math.floor(Math.random() * 100000) + 50000;
          return prev + increment;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isLoading]);

  return (
    <div className="bg-white rounded-lg border border-border-subtle p-6 flex items-center gap-4 hover:shadow-md transition-shadow">
      {/* Icon */}
      <div
        className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${iconBgColor}`}
      >
        <div className={iconColor}>{icon}</div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide mb-1">
          {title}
        </h3>
        {isLoading ? (
          <p className="text-3xl font-bold text-text-primary opacity-60">
            {formatNumberWithCommas(randomValue)}
          </p>
        ) : (
          <p className="text-3xl font-bold text-text-primary">{value}</p>
        )}
      </div>
    </div>
  );
};

export const PurgeStatistics: React.FC<PurgeStatisticsProps> = ({
  lifetimeTotal,
  selectedRangeTotal,
  isLoadingLifetime = false,
  isLoadingSelectedRange = false,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Lifetime Total */}
      <StatCard
        icon={<Database size={24} />}
        title="Lifetime Data Deleted"
        value={formatNumberWithCommas(lifetimeTotal)}
        iconBgColor="bg-blue-50"
        iconColor="text-blue-600"
        isLoading={isLoadingLifetime}
      />

      {/* Selected Range Total */}
      <StatCard
        icon={<Trash size={24} />}
        title="Selected Range Data Deleted"
        value={formatNumberWithCommas(selectedRangeTotal)}
        iconBgColor="bg-green-50"
        iconColor="text-green-600"
        isLoading={isLoadingSelectedRange}
      />
    </div>
  );
};
